import React, { useState, useRef, useCallback } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./CircularProgressChart.css";

const CircularProgressChart = ({ data, size, showLegend = true, className = "" }) => {
  const total = data.reduce((sum, item) => sum + item.number, 0);
  const [tooltip, setTooltip] = useState(null);
  const chartRef = useRef(null);

  const chartData = data.map((item) => {
    const percentage = total > 0 ? (item.number / total) * 100 : 0;
    return {
      name: item.title,
      value: item.number,
      percentage,
      remaining: 100 - percentage,
      color: item.color,
      bg: item.bg,
    };
  });

  const innerRadius = 35;
  const outerRadius = 66;
  const ringCount = data.length;
  const gapBetweenRings = 2;
  const ringThickness = (outerRadius - innerRadius) / ringCount;

  const getPinnedPosition = useCallback((index) => {
    if (!chartRef.current) return { x: 0, y: 0 };

    const rect = chartRef.current.getBoundingClientRect();
    const containerRect = chartRef.current.closest('.progress-chart').getBoundingClientRect();
    const visualIndex = index;

    const ringInner = innerRadius + visualIndex * (ringThickness + gapBetweenRings);
    const ringOuter = ringInner + ringThickness;
    const midRadius = (ringInner + ringOuter) / 2;


    const scale = rect.width / size;
    const scaledRadius = midRadius * scale;

    const chartCenterX = (rect.left - containerRect.left) + (rect.width / 2);
    const chartCenterY = (rect.top - containerRect.top) + (rect.height / 2);

    return {
      x: chartCenterX,
      y: chartCenterY - scaledRadius - 2,
    };
  }, [innerRadius, ringThickness, gapBetweenRings, size, ringCount]);

  return (
    <>
    <div  style={{
        justifyContent: "center",
        borderRadius:"10px",
        alignItems: "center",
        boxShadow: '0px 0px 6px 0px #0000000D',
        outline: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        height: "260px",
        width:"fit-content",
        minWidth:"400px",
        position: "relative",
        border: "1px solid #f0f0f0",
      }}>
     <div style={{ height: "55px",backgroundColor:"#F3F4F5", borderRadius:"10px 10px 0px 0px" }}>
        <h2 style={{fontSize:"16px",fontWeight:"500",marginLeft: "16px",paddingTop: "15px"}}>Course's Progress</h2>
     </div>
    <div
      className={`${className} progress-chart`}
      onMouseDown={e => e.preventDefault()}
      onMouseLeave={() => setTooltip(null)}
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "24px",
        alignItems: "center",
        padding: "16px 24px 24px 16px",
        outline: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        width: 'none',
        position: "relative",
      }}
    >
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x - 22,
            top: tooltip.y - 50,
            backgroundColor: tooltip.color,
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "10px",
            pointerEvents: "none",
            zIndex: 9999,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: "45px",
            minHeight: "40px",
            justifyContent: "center",
            transition: "opacity 0.15s ease",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: 400, lineHeight: 1.2 }}>
            {tooltip.value}
          </span>
          <div
            style={{
              position: "absolute",
              bottom: "-9px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: `10px solid ${tooltip.color}`,
            }}
          />
        </div>
      )}

      <div ref={chartRef} style={{ width: 182, height: 170, outline: 'none' }}>
        <ResponsiveContainer width="100%" height="100%" tabIndex={-1} style={{ outline: 'none' }}>
          <PieChart style={{ outline: 'none' }}>
            {chartData.map((entry, index) => {
              const pieData = [
                { name: "progress", value: entry.percentage },
                { name: "remaining", value: entry.remaining },
              ];

              return (
                <Pie
                  key={`ring-${index}`}
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius + index * (ringThickness + gapBetweenRings)}
                  outerRadius={innerRadius + index * (ringThickness + gapBetweenRings) + ringThickness}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  strokeWidth={0}
                  cornerRadius={10}
                  activeShape={null}
                  style={{ outline: 'none' }}
                  onMouseEnter={(_, cellIndex) => {
                    if (cellIndex === 0) {
                      const pos = getPinnedPosition(index);
                      setTooltip({
                        ...pos,
                        name: entry.name,
                        value: entry.value,
                        color: entry.color,
                      });
                    } else {
                      setTooltip(null);
                    }
                  }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  <Cell fill={entry.color} />
                  <Cell fill={entry.bg} />
                </Pie>
              );
            })}
          </PieChart>
        </ResponsiveContainer>
      </div>

      {showLegend && (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px",width:"150px"}}>
          {chartData.map((segment, index) => (
            <div key={index} style={{ display: "flex", alignItems: "center",justifyContent: "flex-end", gap: "12px" }}>
              <span style={{ color: "rgba(0,0,0,1)", fontSize: "12px", fontWeight: 400 }}>
                {segment.name}
              </span>
              <div style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: segment.color,
              }} />
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
    </>
  );
};

export default CircularProgressChart;